import { useCallback, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useUpdateOwnersListMutation } from '../data-layer/serviceIdentityApi';

/**
 * Saves the service account list for a given service ID.
 *
 * @param serviceId - The ID of the service identity to save accounts for.
 * @returns An object containing `handleUpdateOwnerList`, `isMOSaving`, and `isMOSaveError`.
 */
const useUpdateOwnerList = (serviceId: number | undefined) => {
  const [isMOSaving, setIsMOSaving] = useState(false);

  // API hooks
  const [updateOwnerList, { isError: isMOSaveError }] =
    useUpdateOwnersListMutation();

  const handleUpdateOwnerList = useCallback(
    async (
      owners: { name: string; id: number; rank: number; type: string }[]
    ) => {
      setIsMOSaving(true);

      try {
        if (!serviceId) {
          throw new Error('No service ID provided');
        }

        const payload = { serviceId, owners };

        const controller = updateOwnerList(payload);

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        Logger.log('Failed to save service account list', error);

        return 'Failed to save service account list';
      } finally {
        setIsMOSaving(false);
      }
    },
    [updateOwnerList, serviceId]
  );

  return {
    handleUpdateOwnerList,
    isMOSaveError,
    isMOSaving,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useUpdateOwnerList };
